import PrivacyRequestButton from "./PrivacyRequestButton";
import { PRIVACY_REQUEST_TYPES } from "~/common/lib/privacyRequests";
import styles from "./PrivacyPolicy.module.scss";

export function PrivacyPolicyEN() {
  return (
    <div className={styles.policy}>
      <h3>Privacy Policy</h3>
      <p>
        <strong>Last Updated: October 6, 2026</strong>
      </p>
      <p>
        Palamar Teknoloji Anonim Şirketi, together with its affiliates ("Palmate", "we", "our" or "us"), provides an AI-powered chatbot
        development platform and related software services (collectively, the "Platform"). This Privacy Policy
        ("Policy") describes how we collect, use, disclose and protect information from and about you through our
        Services, our website https://palmate.ai/ or any other website or mobile application that links to this Policy
        (the "Site"), and any other interactions (e.g., customer service and other communications) that you may have
        with Palmate. We refer collectively herein to the Platform, Site, and other interactions you have with us as the
        "Services."
      </p>
      <p>
        Please read this policy carefully to understand how we handle your information. If you do not agree to this
        policy, please do not use the Services.
      </p>

      <h3>Table of Contents</h3>
      <ul>
        <li>Scope of this Policy</li>
        <li>The Information We Collect</li>
        <li>How We Use Your Information</li>
        <li>Legal Bases for Use of Your Information</li>
        <li>Online Analytics and Advertising</li>
        <li>How We Disclose Your Information</li>
        <li>SMS and Mobile Messaging</li>
        <li>Retention of Your Information</li>
        <li>Your Rights and Choices</li>
        <li>Third Party Links and Features</li>
        <li>International Users</li>
        <li>How We Protect Your Information</li>
        <li>Privacy Information for California Users</li>
        <li>Changes to Our Policy</li>
        <li>Google Workspace Data</li>
        <li>Contact Information</li>
      </ul>

      <h3>Scope of this Policy</h3>
      <p>For purposes of this Policy:</p>
      <ul>
        <li>
          An <strong>"Authorized User"</strong> means an individual who accesses the Platform as, or on behalf of, a
          Customer, including Customer personnel and developers.
        </li>
        <li>
          A <strong>"Customer"</strong> means an entity that has contracted with Palmate (such as through an applicable
          Services Agreement or our Terms of Use) to access our Platform for developing and deploying AI chatbots.
        </li>
        <li>
          A <strong>"Customer End User"</strong> means an individual whose information Palmate receives from Customer,
          or otherwise processes on Customer's behalf, in connection with the Platform provided by Palmate to Customer
          pursuant to an applicable Services Agreement. This includes individuals who interact with AI chatbots created
          using our Platform.
        </li>
        <li>
          A <strong>"Site Visitor"</strong> means an individual who visits our Site or otherwise interacts with our
          Services outside the context of our Customers' use of the Platform.
        </li>
      </ul>
      <p>
        This Policy applies to our handling of information about Site Visitors, prospective Customers, and Customers and
        Authorized Users (in relation to their procurement of the Platform and management of their relationship with
        Palmate). We refer collectively to these categories of individuals as "you" throughout this Policy.
      </p>
      <p>
        <strong>Important Note:</strong> This Policy does not cover information about Customer End Users that Palmate
        receives from Customer, or otherwise processes on Customer's behalf, in connection with the Platform provided by
        Palmate to Customer pursuant to an applicable Services Agreement (including the content of conversations with AI
        chatbots ("End User Communications")). Palmate processes End User Communications and other information of
        Customer End Users under the instructions of the relevant Customer, which is the "data controller" (or occupies
        a similar role as defined in applicable privacy laws), as described in the applicable Services Agreement between
        such Customer and Palmate. Palmate's obligations as a "data processor" or "service provider" with respect to
        such information are defined in such Services Agreement and applicable data protection addendum and are not made
        part of this Policy.
      </p>
      <p>
        If you are a Customer End User and you have questions about how your information is collected and processed
        through AI chatbots powered by our Platform, please contact the organization (Palmate Customer) who has provided
        your information to us for more information.
      </p>

      <h4>Enterprise Customer Data and AI Training</h4>
      <p>
        The following commitments apply to enterprise customer data, including end-user conversations, notwithstanding
        the scope exclusions and general data-use provisions elsewhere in this Policy.
      </p>
      <p>
        Palmate does not use enterprise customer data, including conversations, uploaded documents and knowledge-base
        content, to train, fine-tune or improve AI models. This restriction also applies to aggregated or de-identified
        versions of enterprise customer data.
      </p>
      <p>
        Enterprise customer data is processed to deliver the agreed services, in accordance with the customer's
        instructions and the applicable service agreement and Data Processing Addendum.
      </p>

      <h3>The Information We Collect</h3>
      <p>We collect, store, and use certain information from or about you for the purposes described below:</p>
      <h4>Information You Provide To Us</h4>
      <p>
        We collect a variety of information that you provide directly to us. For example, we collect information from
        you when you:
      </p>
      <ul>
        <li>Sign up for and use the Services</li>
        <li>Create, configure, or train AI chatbots using our Platform</li>
        <li>
          Communicate with us (for example, when you communicate with our customer service teams or submit requests or
          questions to us via online forms, email, phone, chat, or otherwise)
        </li>
        <li>Interact with us when we reach out to you by phone or email in our sales and marketing efforts</li>
        <li>Attend our conferences, webinars, or events or interact with us at other conferences or events</li>
        <li>Participate in beta testing or feedback programs</li>
      </ul>
      <p>The types of data we collect directly from you include:</p>
      <ul>
        <li>First and last name</li>
        <li>Business contact information (email address, phone number, postal address)</li>
        <li>Personal contact information (email address, phone number, postal address)</li>
        <li>Username and password associated with the Platform</li>
        <li>Your position and role in your company or organization</li>
        <li>Company information and industry sector</li>
        <li>Chatbot configuration data and training materials you upload</li>
        <li>
          In accordance with applicable law, photos, videos, or audio recordings of sales calls by voice or video chat
        </li>
        <li>Payment and billing information (processed through third-party payment processors)</li>
        <li>Any other information you choose to directly provide to us in connection with your use of the Services</li>
      </ul>
      <h4>Information We Collect Through Automated Means</h4>
      <p>
        When you use our Services, we and our service providers (who are third parties that perform services on our
        behalf) automatically collect certain information about your device and how you use the Services, including:
      </p>
      <ul>
        <li>IP address, browser type, browser language, operating system</li>
        <li>The state or country from which you accessed the Services</li>
        <li>Software and hardware attributes (including device IDs)</li>
        <li>Usage patterns including number of clicks, pages viewed and the order of those pages</li>
        <li>Time spent on particular pages and features</li>
        <li>Date and time you used the Services</li>
        <li>Platform usage analytics (chatbot performance metrics, interaction patterns)</li>
        <li>API usage data and logs</li>
      </ul>
      <p>From your IP address, we may be able to infer your general location (e.g., city/state or postal code).</p>
      <p>
        To collect this information, we and our service providers may use "cookies" or similar tools that track,
        measure, and analyze the behaviors and usage patterns of our users. Cookies are small data files that can be
        stored on your browser and device, so we can recognize you when you return. We use cookies for analytics
        purposes, to deliver certain features of the Services, to help us understand how users engage with the Services,
        and to improve your experience. You may set your web browser to notify you when you receive a cookie and to
        accept or refuse certain cookies. However, if you elect not to accept cookies, some functionality and areas of
        the Services may be restricted. To learn how to manage your cookies, please follow the instructions from your
        specific browser, or if accessing the Services via a mobile device, refer to the manufacturer's instructions.
        For more details, please refer to our Cookie Policy.
      </p>
      <h4>Information We Collect From Others</h4>
      <p>
        From time to time, we may collect information about you from other sources, including our affiliates, marketing
        vendors, and third-party databases. We use this information to supplement the information that we collect
        directly from you and for any of the purposes described in the next section.
      </p>
      <p>
        When you "like" or "follow" us on LinkedIn, Twitter, GitHub or other social media sites, we may collect some
        information from you including your name, email address, and any comments or content you post relevant to us.
      </p>

      <h3>How We Use Your Information</h3>
      <p>
        We use your information for various purposes depending on the types of information we have collected from and
        about you to:
      </p>
      <ul>
        <li>Provide and maintain our Services, including hosting and operating the Platform</li>
        <li>Enable you to create, configure, deploy, and manage AI chatbots</li>
        <li>Process transactions and manage your account</li>
        <li>
          Respond to your inquiries and requests for information, and provide you with more effective and efficient
          customer service
        </li>
        <li>Provide technical support and troubleshooting</li>
        <li>
          Engage in analysis and research regarding use of the Services, improve the Services, and develop new AI
          features and capabilities
        </li>
        <li>
          Train and improve our AI models and algorithms using only aggregated, de-identified data, excluding
          enterprise customer data and Google Workspace data
        </li>
        <li>Measure the effectiveness of our advertising and deliver relevant advertising to you</li>
        <li>Suggest Services that may be of interest to you</li>
        <li>Send you service-related communications and updates</li>
        <li>Secure our Services and resolve technical issues being reported</li>
        <li>
          Comply with any procedures, laws, and regulations which apply to us where it is necessary for our legitimate
          interests or the legitimate interests of others
        </li>
        <li>
          Establish, exercise, or defend our legal rights where it is necessary for our legitimate interests or the
          legitimate interests of others
        </li>
      </ul>
      <p>
        <strong>Aggregate/De-Identified Information:</strong> We may aggregate and/or de-identify any information
        collected through our Services so that such information can no longer be linked to you or your device
        ("Aggregate/De-Identified Information"). We may use such information for any purpose, including without
        limitation for research and marketing purposes, AI model improvement, and may also share such data with any
        third parties, including advertisers, partners, and sponsors. These permissions do not override the
        enterprise customer data and Google Workspace data restrictions in this Policy.
      </p>

      <h3>Legal Bases For Use Of Your Information</h3>
      <p>The legal bases for using your information as set out in this Policy are as follows:</p>
      <ul>
        <li>
          Where we need to perform the contract we are about to enter into or have entered into with you for the
          Services
        </li>
        <li>
          Where it is necessary for our legitimate interests (or those of a third party) and your interests and
          fundamental rights do not override those interests
        </li>
        <li>Where we need to comply with a legal or regulatory obligation</li>
        <li>Where we have your consent to process your information in a certain way</li>
      </ul>

      <h3>Online Analytics And Advertising</h3>
      <h4>Analytics</h4>
      <p>
        We may use third-party web analytics services (such as those of Google Analytics) on our Site and Services to
        collect and analyze usage information through cookies and similar tools; engage in auditing, research, or
        reporting; assist with fraud prevention; and provide certain features to you. To prevent Google Analytics from
        using your information for analytics, you may install the Google Analytics Opt-out Browser Add-on by visiting
        the Google Analytics opt-out page. If you receive email from us, we may use certain analytics tools, such as
        clear GIFs to capture data such as when you open our message or click on any links or banners our email
        contains. This data allows us to gauge the effectiveness of our communications and marketing campaigns.
      </p>
      <h4>Online Advertising</h4>
      <p>
        In using the Site, we may allow select third party advertising technology partners to place cookies or other
        tracking technologies on the browser of your device to collect information about you as discussed above. These
        third parties (e.g., ad networks and ad servers) may use this information to serve relevant content and
        advertising to you as you browse the Internet, and access their own cookies or other tracking technologies on
        your browser to assist in this activity. If you are interested in more information about these online
        advertising activities, and how you can generally control cookies from being put on your computer to deliver
        tailored advertising, you may visit the Network Advertising Initiative's Consumer Opt-Out link, the Digital
        Advertising Alliance's Consumer Opt-Out link, or Your Online Choices to opt-out of receiving tailored
        advertising from companies that participate in those programs.
      </p>
      <h4>Meta Pixel (Facebook and Instagram)</h4>
      <p>
        We use Meta Pixel on palmate.ai to measure advertising performance and attribute website visits and enquiries
        to campaigns. It loads only after you consent to marketing cookies. Consent to analytics alone does not enable
        it, and simply browsing the Site does not constitute consent.
      </p>
      <p>
        With that consent, Meta receives page-view events (PageView) and an enquiry event (Lead) when a demo, quote or
        contact form is successfully submitted. Data may include the page URL, event time, IP address, browser and
        device information, cookie or advertising-click identifiers, form category and a request identifier used to
        prevent duplicate events. We do not include form contents, names, email addresses, phone numbers or messages
        in our custom Pixel event data.
      </p>
      <p>
        Meta may associate these events with information it holds, including a Facebook or Instagram account, and use
        them for measurement, audience creation and personalised advertising under its policies. Meta may process the
        information outside Turkey, including in the United States. See the{" "}
        <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta Privacy Policy</a>{" "}
        for its processing and your choices.
      </p>
      <p>
        You can withdraw marketing consent at any time through the cookie preferences control in the Site footer.
        This stops subsequent Pixel events from this Site without preventing access to the Site or its forms.
        Withdrawal does not automatically delete previously stored cookies or information already received by Meta.
        Our Cookie Policy explains the relevant cookies and how to delete them.
      </p>
      <h4>Visitor Identification and Tracking</h4>
      <p>
        When you visit or log in to our website, cookies and similar technologies may be used by our online data
        partners or vendors to associate these activities with other personal information they or others have about you,
        including by association with your email. We (or service providers on our behalf) may then send communications
        and marketing to these email addresses. To ask us to stop these messages, use the{" "}
        <PrivacyRequestButton requestType={PRIVACY_REQUEST_TYPES.MARKETING_OPT_OUT}/> form.
        You can also submit a request to{" "}
        <PrivacyRequestButton requestType={PRIVACY_REQUEST_TYPES.DATA_OBJECTION}/>.
        Our team will review your request and contact you about the outcome.
      </p>

      <h3>How We Disclose Your Information</h3>
      <p>We will disclose your information in the following ways:</p>
      <ul>
        <li>
          <strong>Service Providers</strong>: We provide access to or disclose your information to select third parties
          who help us deliver or improve our Site and Services or perform services on our behalf, including cloud
          hosting, billing and payment, advertising and marketing, analytics, fraud prevention, research, data storage
          or processing, security, AI model training services, and consulting and legal services. Enterprise customer
          data and Google Workspace data are excluded from disclosures for AI model training.
        </li>
        <li>
          <strong>Affiliates and Subsidiaries</strong>: We may disclose the information we collect within the Palmate
          family of companies for the purposes outlined in this Policy.
        </li>
        <li>
          <strong>Your Company</strong>: If you are an Authorized User, we may provide your information to the company
          you are engaged or employed by to fulfill and enforce our applicable Services Agreement with your company, and
          to inform your company regarding usage, support, or training needs.
        </li>
        <li>
          <strong>Protection of Palmate and Others</strong>: By using the Site or Services, you acknowledge and agree
          that we may access, retain, and disclose the information we collect and maintain about you if required to do
          so by applicable law or in a good faith belief that such access, retention or disclosure is reasonably
          necessary to: (a) comply with legal process (e.g. a subpoena or court order); (b) enforce any contracts with
          you; (c) respond to claims that any content violates the rights of third parties; and/or (d) protect the
          rights, property or personal safety of Palmate, its agents and affiliates, its users and/or the public.
        </li>
        <li>
          <strong>Business Transactions</strong>: In accordance with applicable legal obligations, your information may
          be provided to third parties in connection with a merger or acquisition (including transfers made as part of
          insolvency or bankruptcy proceedings) involving all or part of Palmate or our assets, or as part of a
          corporate reorganization or stock sale or other change in corporate control or business change, including for
          the purpose of determining whether to proceed or continue with such transaction or business relationship.
        </li>
        <li>
          <strong>Aggregate/De-Identified Information</strong>: From time to time, we may disclose
          Aggregate/De-identified Information about use of the Site or Services or our user base with partners and
          others in our discretion, but such information will not identify you personally.
        </li>
        <li>
          <strong>With Your Consent or at Your Direction</strong>: In addition to the disclosures described in this
          Policy, we may disclose information about you to third parties whenever you consent to or direct such sharing.
        </li>
      </ul>

      <h3>SMS and Mobile Messaging</h3>
      <p>
        Palamar Teknoloji Anonim Şirketi, doing business as Palmate, may collect mobile phone numbers to provide
        account authentication, identity verification, one-time passcodes (OTPs), security alerts, account and service
        notifications, customer support communications, and other transactional messaging requested by the user.
        Message frequency varies based on user activity, and message and data rates may apply.
      </p>
      <p>
        Where a user separately provides express consent to receive marketing messages, Palmate may also send product or
        service announcements, offers, promotions, event invitations, campaign messages, or similar marketing
        communications. Consent to receive marketing SMS messages is optional and is not a condition of purchasing or
        using Palmate services. Marketing messages are sent only to users who have completed the applicable opt-in
        process, and users may withdraw that consent at any time by replying STOP.
      </p>
      <p>
        We do not sell or share your SMS opt-in data or personal information with third parties for marketing purposes.
        We also do not share mobile phone numbers, SMS consent records, or opt-in data with affiliates or third parties
        for their own marketing or promotional purposes.
      </p>

      <h3>Retention Of Your Information</h3>
      <p>
        We keep your information for no longer than necessary for the purposes for which it is processed. The length of
        time for which we retain information depends on the purposes for which we collect and use it and/or as required
        to comply with applicable laws and our legal obligations.
      </p>

      <h3>Your Rights And Choices</h3>
      <p>You have certain rights with respect to your information as further described in this section.</p>
      <h4>Your Legal Rights</h4>
      <p>
        If you would like further information in relation to your legal rights under applicable law or would like to
        exercise any of them, please contact us using the information in the "Contact Information" section below at any
        time. Your local laws (e.g., if you are a citizen or resident of the European Economic Area, California,
        Connecticut, or other locations with similar laws) may permit you to request that we:
      </p>
      <ul>
        <li>Provide access to and/or a copy of certain information we hold about you</li>
        <li>Update information which is out of date or incorrect</li>
        <li>Delete certain information that we are holding about you</li>
        <li>Restrict the way that we process and disclose certain of your information</li>
        <li>Port your data to another service provider</li>
      </ul>
      <p>
        Such laws may also permit you to revoke your consent to the processing of your information for certain purposes.
        We will consider all requests and provide our response within the time period stated by applicable law and as
        otherwise required by applicable law.
      </p>
      <h4>Marketing Communications</h4>
      <p>
        If, in accordance with applicable legal requirements, we send you marketing communications regarding our
        Services or the services of third parties that we believe will be interesting to you, you can ask us to stop
        sending such communications at any time by contacting us using the information in the "Contact Information"
        section below. In our marketing email messages, you can also opt out by following the instructions located at
        the bottom of such emails.
      </p>

      <h3>Third Party Links And Features</h3>
      <p>
        Our Services may contain links to third-party websites, including AI model providers, cloud services, and
        integration partners. If you choose to visit these sites and use their services, please note that we are not
        responsible for their content or privacy practices. The collection, use, and disclosure of your information will
        be subject to the privacy policies of the third-party websites, and not this Policy.
      </p>

      <h3>International Users</h3>
      <p>
        Your information is maintained and processed by us and our third-party service providers in Turkey, the United
        States, and elsewhere. Thus, your information may be maintained, processed, and stored in jurisdictions that may
        have different data protection laws than those in your country of residence. In the event that your information
        is transferred in these ways, please note that we comply with applicable legal requirements governing the
        transfer of information across borders. By using the Services, you agree to and acknowledge these transfers.
      </p>

      <h3>How We Protect Your Information</h3>
      <p>
        Palmate takes a variety of technical and organizational security measures to protect the information provided to
        us from loss, misuse, and unauthorized access, disclosure, alteration, or destruction. These measures include
        encryption, access controls, secure development practices, and regular security assessments. However, no
        Internet or email transmission is ever fully secure or error free. Please keep this in mind when disclosing any
        information to us online.
      </p>

      <h3>Privacy Information for California Users</h3>
      <p>
        If you are a California resident, the California Consumer Privacy Act ("CCPA") requires us to provide you with
        additional information regarding your rights with respect to your "personal information" as defined in the CCPA.
      </p>
      <h4>Categories of Personal Information We Collect</h4>
      <div className={styles.tableWrapper}>
        <table>
          <thead>
            <tr>
              <th>Category of Personal Information</th>
              <th>How We Use this Personal Information</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Contact Information (such as your full name, phone number, address, email address)</td>
              <td>
                • Provide the Services and respond to your requests
                <br />• Contact you with relevant information and offers
                <br />• Analyze use of and improve the services
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
            <tr>
              <td>Login Information (such as your username and password)</td>
              <td>
                • Provide the Services and respond to your requests
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
            <tr>
              <td>
                Professional or Employment Information (such as the name and address of the company you work for and
                your title)
              </td>
              <td>
                • Provide the services and respond to your requests
                <br />• Contact you with relevant information and offers
                <br />• Analyze use of and improve the services
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
            <tr>
              <td>
                Device and Online Information (such as mobile device information, IP address, and information about your
                interaction with the Services)
              </td>
              <td>
                • Provide the Site and Services and respond to your requests
                <br />• Contact you with relevant information and offers
                <br />• Analyze use of and improve the services
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
            <tr>
              <td>Geolocation data</td>
              <td>
                • Provide the Site and Services and respond to your requests
                <br />• Contact you with relevant information and offers
                <br />• Analyze use of and improve the services
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
            <tr>
              <td>Commercial Information (such as chatbot configuration data, usage patterns)</td>
              <td>
                • Provide the Site and Services and respond to your requests
                <br />• Improve our AI technology and services
                <br />• Analyze use of and improve the services
                <br />• Comply with law or defend our legal rights
                <br />• Security/fraud prevention
                <br />• Create aggregate/de-identified information
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <h4>Your California Privacy Rights</h4>
      <p>
        The CCPA allows you to make certain requests about your personal information, including requests to access,
        delete, correct, and obtain information about our data practices. To exercise these rights, please contact us
        using the contact information provided below.
      </p>

      <h3>Changes To Our Policy</h3>
      <p>
        We reserve the right to amend this Policy at any time to reflect changes in the law, our data collection and use
        practices, or advances in technology. We will make the revised Policy accessible on the Services, so you should
        review the Policy periodically. You can know if the Policy has changed since the last time you reviewed it by
        checking the "Last Updated" date at the beginning of the document. If we make a material change to the Policy,
        you will be provided with appropriate notice in accordance with legal requirements.
      </p>

      <h3>GOOGLE WORKSPACE DATA</h3>
      <p>
        When you connect a Gmail mailbox to Palmate, Palmate's use of information received from Google APIs adheres to
        the Google API Services User Data Policy, including the Limited Use requirements.
      </p>
      <p>Notwithstanding anything else in this policy, and specifically:</p>
      <ul>
        <li>
          We do not use, transfer or sell Google Workspace data (whether raw, aggregated, de-identified or derived)
          to create, train or improve any generalized or foundational machine learning or artificial intelligence
          model, whether our own or a third party's.
        </li>
        <li>
          Google Workspace data is excluded from the aggregated and de-identified analytics described elsewhere in
          this policy, and from every purpose in this policy other than those listed immediately below.
        </li>
        <li>
          We process Google Workspace data only to provide the features you have asked for: presenting the email your
          customers send you to your own support agents, drafting and sending replies from your mailbox, and retaining
          the original message so it can be produced later.
        </li>
        <li>
          We transfer the content of Google Workspace messages to Google's Gemini API (model gemini-3.1-flash-lite)
          on a paid tier, under terms in which Google does not use that content to train or improve its models.
          We transfer it to no other artificial-intelligence or machine-learning provider.
        </li>
        <li>
          We do not transfer Google Workspace data to any person outside the users of your own Palmate workspace,
          except where we are required to by law.
        </li>
        <li>
          We retain the original of each message for 2 years, after which it is deleted. Disconnecting the mailbox
          deletes the stored credentials and ends our access immediately.
        </li>
      </ul>

      <h3>Contact Information</h3>
      <p>
        Palmate welcomes your comments and questions regarding this Policy and the collection and use of your
        information. If you have questions or concerns, please email us at
        <a href="mailto:privacy@palmate.ai"> privacy@palmate.ai</a> or write to our Data Protection Officer at:
      </p>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
        <br />
        Privacy Department
        <br />
        Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul
      </p>
      <p>
        For users in the European Economic Area, you may also contact our Data Protection Officer at:
        <a href="mailto:dpo@palmate.ai"> dpo@palmate.ai</a>
      </p>
      <hr />
      <p>
        <em>
          This Privacy Policy is effective as of the date set forth above and applies to all information previously
          obtained by Palmate in connection with the Services.
        </em>
      </p>
    </div>
  );
}
export function PrivacyPolicyTR() {
  return (
    <div className={styles.policy}>
      <h3>Gizlilik Politikası</h3>
      <p>
        <strong>Son Güncelleme: 6 Ekim 2026</strong>
      </p>
      <p>
        Palamar Teknoloji Anonim Şirketi, iştirakleri ile birlikte ("Palmate", "biz", "bizim" veya "bize"), yapay zeka destekli chatbot
        geliştirme platformu ve ilgili yazılım hizmetleri (toplu olarak "Platform") sunmaktadır. Bu Gizlilik Politikası
        ("Politika"), Hizmetlerimiz, web sitemiz https://palmate.ai/ veya bu Politikaya bağlantı veren diğer web
        siteleri veya mobil uygulamalar ("Site") ve Palmate ile sahip olabileceğiniz diğer etkileşimler (örn. müşteri
        hizmetleri ve diğer iletişimler) aracılığıyla sizden ve sizinle ilgili bilgileri nasıl topladığımızı,
        kullandığımızı, ifşa ettiğimizi ve koruduğumuzu açıklamaktadır. Platforma, Siteye ve bizimle olan diğer
        etkileşimlerinize toplu olarak "Hizmetler" olarak atıfta bulunuyoruz.
      </p>
      <p>
        Bilgilerinizi nasıl ele aldığımızı anlamak için lütfen bu politikayı dikkatlice okuyun. Bu politikayı kabul
        etmiyorsanız, lütfen Hizmetleri kullanmayın.
      </p>

      <h3>İçindekiler</h3>
      <ol>
        <li>Bu Politikanın Kapsamı</li>
        <li>Topladığımız Bilgiler</li>
        <li>Bilgilerinizi Nasıl Kullanıyoruz</li>
        <li>Bilgilerinizin Kullanımı İçin Yasal Dayanaklar</li>
        <li>Çevrimiçi Analitik ve Reklamcılık</li>
        <li>Bilgilerinizi Nasıl İfşa Ediyoruz</li>
        <li>SMS ve Mobil Mesajlaşma</li>
        <li>Bilgilerinizin Saklanması</li>
        <li>Haklarınız ve Seçimleriniz</li>
        <li>Üçüncü Taraf Bağlantıları ve Özellikleri</li>
        <li>Uluslararası Kullanıcılar</li>
        <li>Bilgilerinizi Nasıl Koruyoruz</li>
        <li>Kaliforniya Kullanıcıları İçin Gizlilik Bilgileri</li>
        <li>Politikamızdaki Değişiklikler</li>
        <li>Google Workspace Verileri</li>
        <li>İletişim Bilgileri</li>
      </ol>

      <h3>1. Bu Politikanın Kapsamı</h3>
      <p>Bu Politika kapsamında:</p>
      <ul>
        <li>
          <strong>"Yetkili Kullanıcı"</strong>, Müşteri personeli ve geliştiriciler dahil olmak üzere, bir Müşteri
          olarak veya onun adına Platforma erişen bir kişi anlamına gelir.
        </li>
        <li>
          <strong>"Müşteri"</strong>, yapay zeka chatbotları geliştirmek ve dağıtmak için Platformumuza erişmek üzere
          Palmate ile sözleşme yapmış (Hizmet Sözleşmesi veya Kullanım Koşullarımız aracılığıyla) bir kuruluş anlamına
          gelir.
        </li>
        <li>
          <strong>"Müşteri Son Kullanıcısı"</strong>, Palmate'in Müşteriden aldığı veya geçerli bir Hizmet Sözleşmesi
          uyarınca Müşteri adına işlediği bir kişi anlamına gelir. Bu, Platformumuz kullanılarak oluşturulan yapay zeka
          chatbotları ile etkileşime giren kişileri içerir.
        </li>
        <li>
          <strong>"Site Ziyaretçisi"</strong>, Sitemizi ziyaret eden veya Müşterilerimizin Platform kullanımı bağlamı
          dışında Hizmetlerimizle etkileşime giren bir kişi anlamına gelir.
        </li>
      </ul>
      <p>
        Bu Politika, Site Ziyaretçileri, potansiyel Müşteriler ve Müşteriler ve Yetkili Kullanıcılar (Platformun satın
        alınması ve Palmate ile ilişkilerinin yönetimiyle ilgili olarak) hakkındaki bilgilerin işlenmesine uygulanır. Bu
        Politika boyunca bu kişi kategorilerine toplu olarak "siz" olarak atıfta bulunuyoruz.
      </p>
      <p>
        <strong>Önemli Not:</strong> Bu Politika, Palmate'in Müşteriden aldığı veya geçerli bir Hizmet Sözleşmesi
        uyarınca Müşteri adına işlediği Müşteri Son Kullanıcıları hakkındaki bilgileri (yapay zeka chatbotları ile
        yapılan konuşmaların içeriği dahil ("Son Kullanıcı İletişimleri")) kapsamaz. Palmate, Son Kullanıcı
        İletişimlerini ve Müşteri Son Kullanıcılarının diğer bilgilerini, ilgili Müşterinin talimatları doğrultusunda
        işler; Müşteri, yürürlükteki gizlilik yasalarında tanımlandığı şekliyle "veri sorumlusu" (veya benzer bir rol)
        konumundadır. Palmate'in bu tür bilgilerle ilgili "veri işleyici" veya "hizmet sağlayıcı" olarak yükümlülükleri,
        Hizmet Sözleşmesinde ve geçerli veri koruma ekinde tanımlanmıştır ve bu Politikanın bir parçası değildir.
      </p>
      <p>
        Eğer bir Müşteri Son Kullanıcısıysanız ve bilgilerinizin Platformumuz tarafından desteklenen yapay zeka
        chatbotları aracılığıyla nasıl toplandığı ve işlendiği hakkında sorularınız varsa, lütfen daha fazla bilgi için
        bilgilerinizi bize sağlayan kuruluşla (Palmate Müşterisi) iletişime geçin.
      </p>

      <h4>Kurumsal Müşteri Verileri ve Yapay Zekâ Eğitimi</h4>
      <p>
        Aşağıdaki taahhütler, bu Politikanın diğer bölümlerindeki kapsam istisnaları ve genel veri kullanım hükümlerine
        bakılmaksızın, son kullanıcı konuşmaları dahil kurumsal müşteri verileri için geçerlidir.
      </p>
      <p>
        Palmate; konuşmalar, yüklenen belgeler ve bilgi tabanı içerikleri dahil kurumsal müşteri verilerini yapay zekâ
        modellerini eğitmek, ince ayar yapmak veya geliştirmek için kullanmaz. Bu kısıtlama, kurumsal müşteri verilerinin
        toplulaştırılmış veya kimlikten arındırılmış biçimleri için de geçerlidir.
      </p>
      <p>
        Kurumsal müşteri verileri, müşterinin talimatları ve geçerli hizmet sözleşmesi ile Veri İşleme Eki (DPA)
        doğrultusunda, kararlaştırılan hizmetleri sunmak amacıyla işlenir.
      </p>

      <h3>2. Topladığımız Bilgiler</h3>
      <p>
        Aşağıda açıklanan amaçlar doğrultusunda sizden veya sizinle ilgili belirli bilgileri topluyoruz, saklıyoruz ve
        kullanıyoruz:
      </p>
      <h4>Bize Doğrudan Sağladığınız Bilgiler</h4>
      <p>
        Doğrudan bize sağladığınız çeşitli bilgileri topluyoruz. Örneğin, aşağıdaki durumlarda sizden bilgi topluyoruz:
      </p>
      <ul>
        <li>Hizmetlere kaydolduğunuzda ve kullandığınızda</li>
        <li>
          Platformumuzu kullanarak yapay zeka chatbotları oluşturduğunuzda, yapılandırdığınızda veya eğittiğinizde
        </li>
        <li>
          Bizimle iletişim kurduğunuzda (örneğin, müşteri hizmetleri ekiplerimizle iletişim kurduğunuzda veya çevrimiçi
          formlar, e-posta, telefon, sohbet veya başka yollarla bize talep veya sorular gönderdiğinizde)
        </li>
        <li>
          Satış ve pazarlama çabalarımızda telefon veya e-posta ile size ulaştığımızda bizimle etkileşime girdiğinizde
        </li>
        <li>
          Konferanslarımıza, web seminerlerimize veya etkinliklerimize katıldığınızda veya diğer konferans veya
          etkinliklerde bizimle etkileşime girdiğinizde
        </li>
        <li>Beta test veya geri bildirim programlarına katıldığınızda</li>
      </ul>
      <p>Doğrudan sizden topladığımız veri türleri şunları içerir:</p>
      <ul>
        <li>Ad ve soyad</li>
        <li>İş iletişim bilgileri (e-posta adresi, telefon numarası, posta adresi)</li>
        <li>Kişisel iletişim bilgileri (e-posta adresi, telefon numarası, posta adresi)</li>
        <li>Platform ile ilişkili kullanıcı adı ve şifre</li>
        <li>Şirketiniz veya kuruluşunuzdaki pozisyonunuz ve rolünüz</li>
        <li>Şirket bilgileri ve sektör</li>
        <li>Yüklediğiniz chatbot yapılandırma verileri ve eğitim materyalleri</li>
        <li>
          Yürürlükteki yasalara uygun olarak, sesli veya görüntülü sohbet yoluyla yapılan satış görüşmelerinin fotoğraf,
          video veya ses kayıtları
        </li>
        <li>Ödeme ve fatura bilgileri (üçüncü taraf ödeme işlemcileri aracılığıyla işlenir)</li>
        <li>Hizmetleri kullanımınızla bağlantılı olarak bize doğrudan sağlamayı seçtiğiniz diğer bilgiler</li>
      </ul>
      <h4>Otomatik Araçlarla Topladığımız Bilgiler</h4>
      <p>
        Hizmetlerimizi kullandığınızda, biz ve hizmet sağlayıcılarımız (bizim adımıza hizmet sunan üçüncü taraflar)
        cihazınız ve Hizmetleri nasıl kullandığınız hakkında otomatik olarak belirli bilgileri toplarız:
      </p>
      <ul>
        <li>IP adresi, tarayıcı türü, tarayıcı dili, işletim sistemi</li>
        <li>Hizmetlere eriştiğiniz eyalet veya ülke</li>
        <li>Yazılım ve donanım özellikleri (cihaz kimlikleri dahil)</li>
        <li>Tıklama sayısı, görüntülenen sayfalar ve bu sayfaların sırası dahil kullanım kalıpları</li>
        <li>Belirli sayfa ve özelliklerde geçirilen süre</li>
        <li>Hizmetleri kullandığınız tarih ve saat</li>
        <li>Platform kullanım analitiği (chatbot performans metrikleri, etkileşim kalıpları)</li>
        <li>API kullanım verileri ve kayıtları</li>
      </ul>
      <p>IP adresinizden, genel konumunuzu (örn. şehir/eyalet veya posta kodu) çıkarabiliriz.</p>
      <p>
        Bu bilgileri toplamak için biz ve hizmet sağlayıcılarımız, kullanıcılarımızın davranışlarını ve kullanım
        kalıplarını izleyen, ölçen ve analiz eden "çerezler" veya benzer araçlar kullanabiliriz. Çerezler, tarayıcınızda
        ve cihazınızda saklanabilen küçük veri dosyalarıdır, böylece döndüğünüzde sizi tanıyabiliriz. Çerezleri analitik
        amaçlarla, Hizmetlerin belirli özelliklerini sunmak, kullanıcıların Hizmetlerle nasıl etkileşime girdiğini
        anlamamıza yardımcı olmak ve deneyiminizi iyileştirmek için kullanırız. Web tarayıcınızı bir çerez aldığınızda
        sizi bilgilendirecek ve belirli çerezleri kabul etmek veya reddetmek üzere ayarlayınız. Ancak çerezleri kabul
        etmemeyi seçerseniz, Hizmetlerin bazı işlevleri ve alanları kısıtlanabilir. Çerezlerinizi nasıl yöneteceğinizi
        öğrenmek için lütfen belirli tarayıcınızdan talimatları izleyin veya Hizmetlere bir mobil cihaz üzerinden
        erişiyorsanız, üreticinin talimatlarına bakın. Daha fazla ayrıntı için lütfen Çerez Politikamıza bakın.
      </p>
      <h4>Başkalarından Topladığımız Bilgiler</h4>
      <p>
        Zaman zaman, iştiraklarımız, pazarlama satıcıları ve üçüncü taraf veritabanları dahil olmak üzere diğer
        kaynaklardan sizinle ilgili bilgi toplayabiliriz. Bu bilgileri, doğrudan sizden topladığımız bilgileri
        tamamlamak ve bir sonraki bölümde açıklanan amaçlardan herhangi biri için kullanırız.
      </p>
      <p>
        LinkedIn, Twitter, GitHub veya diğer sosyal medya sitelerinde bizi "beğendiğinizde" veya "takip ettiğinizde",
        adınız, e-posta adresiniz ve bizimle ilgili yayınladığınız yorumlar veya içerikler dahil olmak üzere bazı
        bilgilerinizi toplayabiliriz.
      </p>

      <h3>3. Bilgilerinizi Nasıl Kullanıyoruz</h3>
      <p>
        Bilgilerinizi, sizden ve sizinle ilgili topladığımız bilgi türlerine bağlı olarak çeşitli amaçlar için
        kullanırız:
      </p>
      <ul>
        <li>Platformu barındırmak ve işletmek dahil Hizmetlerimizi sağlamak ve sürdürmek</li>
        <li>Yapay zeka chatbotları oluşturmanızı, yapılandırmanızı, dağıtmanızı ve yönetmenizi sağlamak</li>
        <li>İşlemleri işlemek ve hesabınızı yönetmek</li>
        <li>
          Sorularınıza ve bilgi taleplerinize yanıt vermek ve size daha etkili ve verimli müşteri hizmeti sağlamak
        </li>
        <li>Teknik destek ve sorun giderme sağlamak</li>
        <li>
          Hizmetlerin kullanımına ilişkin analiz ve araştırma yapmak, Hizmetleri iyileştirmek ve yeni yapay zeka
          özellikleri ve yetenekleri geliştirmek
        </li>
        <li>
          Kurumsal müşteri verileri ve Google Workspace verileri hariç olmak üzere, yalnızca toplulaştırılmış,
          kimlikten arındırılmış verilerle yapay zekâ modellerimizi ve algoritmalarımızı eğitmek ve iyileştirmek
        </li>
        <li>Reklamlarımızın etkinliğini ölçmek ve size alakalı reklamlar sunmak</li>
        <li>İlginizi çekebilecek Hizmetleri önermek</li>
        <li>Hizmetle ilgili iletişimler ve güncellemeler göndermek</li>
        <li>Hizmetlerimizi güvence altına almak ve bildirilen teknik sorunları çözmek</li>
        <li>
          Meşru çıkarlarımız veya başkalarının meşru çıkarları için gerekli olduğunda, bizim için geçerli olan prosedür,
          yasa ve yönetmeliklere uymak
        </li>
        <li>
          Meşru çıkarlarımız veya başkalarının meşru çıkarları için gerekli olduğunda yasal haklarımızı oluşturmak,
          kullanmak veya savunmak
        </li>
      </ul>
      <p>
        <strong>Toplu/Kimliksizleştirilmiş Bilgiler:</strong> Hizmetlerimiz aracılığıyla toplanan bilgileri, artık size
        veya cihazınıza bağlanamayacak şekilde toplayabilir ve/veya kimliksizleştirebiliriz ("Toplu/Kimliksizleştirilmiş
        Bilgiler"). Bu tür bilgileri, araştırma ve pazarlama amaçları, yapay zeka modeli iyileştirme dahil olmak üzere
        herhangi bir amaç için sınırlama olmaksızın kullanabiliriz ve ayrıca bu verileri reklamverenler, ortaklar ve
        sponsorlar dahil üçüncü taraflarla paylaşabiliriz. Bu izinler, bu Politikadaki kurumsal müşteri verileri ve
        Google Workspace verileri için geçerli kısıtlamaları ortadan kaldırmaz.
      </p>

      <h3>4. Bilgilerinizin Kullanımı İçin Yasal Dayanaklar</h3>
      <p>Bilgilerinizi bu Politikada belirtildiği şekilde kullanmamızın yasal dayanakları şunlardır:</p>
      <ul>
        <li>Hizmetler için sizinle yapmak üzere olduğumuz veya yaptığımız sözleşmeyi yerine getirmemiz gerektiğinde</li>
        <li>
          Meşru çıkarlarımız (veya üçüncü bir tarafın çıkarları) için gerekli olduğunda ve sizin çıkarlarınız ve temel
          haklarınız bu çıkarları geçersiz kılmadığında
        </li>
        <li>Yasal veya düzenleyici bir yükümlülüğe uymamız gerektiğinde</li>
        <li>Bilgilerinizi belirli bir şekilde işlemek için onayınız olduğunda</li>
      </ul>

      <h3>5. Çevrimiçi Analitik ve Reklamcılık</h3>
      <h4>Analitik</h4>
      <p>
        Sitemizde ve Hizmetlerimizde (Google Analytics gibi) üçüncü taraf web analitik hizmetlerini kullanabilir ve
        çerezler ve benzer araçlar aracılığıyla kullanım bilgilerini toplayıp analiz edebiliriz; denetim, araştırma veya
        raporlama yapabiliriz; dolandırıcılığın önlenmesine yardımcı olabiliriz; ve size belirli özellikler
        sağlayabiliriz. Google Analytics'in bilgilerinizi analitik için kullanmasını önlemek için Google Analytics Devre
        Dışı Bırakma Tarayıcı Eklentisini Google Analytics devre dışı bırakma sayfasını ziyaret ederek
        yükleyebilirsiniz. Bizden e-posta alırsanız, mesajımızı ne zaman açtığınız veya e-postamızın içerdiği herhangi
        bir bağlantıya veya bannera tıkladığınız gibi verileri yakalamak için net GIF'ler gibi belirli analitik araçları
        kullanabiliriz. Bu veriler, iletişimlerimizin ve pazarlama kampanyalarımızın etkinliğini ölçmemize olanak tanır.
      </p>
      <h4>Çevrimiçi Reklamcılık</h4>
      <p>
        Siteyi kullanırken, yukarıda tartışıldığı gibi cihazınızın tarayıcısında sizinle ilgili bilgi toplamak için
        belirli üçüncü taraf reklam teknolojisi ortaklarının çerez veya diğer izleme teknolojilerini yerleştirmesine
        izin verebiliriz. Bu üçüncü taraflar (örn. reklam ağları ve reklam sunucuları), İnternette gezinirken size
        alakalı içerik ve reklamlar sunmak için bu bilgileri kullanabilir ve bu faaliyete yardımcı olmak için
        tarayıcınızda kendi çerezlerine veya diğer izleme teknolojilerine erişebilirler. Bu çevrimiçi reklam
        faaliyetleri hakkında daha fazla bilgi almak ve bilgisayarınıza özel reklam sunmak için çerezlerin nasıl genel
        olarak kontrol edilebileceği konusunda bilgi almak istiyorsanız, Network Advertising Initiative'in Tüketici
        Devre Dışı Bırakma bağlantısını, Digital Advertising Alliance'ın Tüketici Devre Dışı Bırakma bağlantısını veya
        bu programlara katılan şirketlerden özel reklamlar almayı devre dışı bırakmak için Your Online Choices'ı ziyaret
        edebilirsiniz.
      </p>
      <h4>Meta Pixel (Facebook ve Instagram)</h4>
      <p>
        palmate.ai üzerinde reklam performansını ölçmek, site ziyaretlerini ve talepleri kampanyalarla ilişkilendirmek
        amacıyla Meta Pixel kullanıyoruz. Pixel yalnızca pazarlama çerezlerine açık rıza vermenizden sonra yüklenir.
        Yalnızca analitik izni vermeniz Pixel'i etkinleştirmez ve siteyi ziyaret etmeniz tek başına rıza sayılmaz.
      </p>
      <p>
        Bu izin kapsamında Meta'ya sayfa görüntüleme olayları (PageView) ve demo, teklif veya iletişim formunun başarıyla
        gönderilmesi üzerine bir talep olayı (Lead) iletilir. Veriler; sayfa adresi, olay zamanı, IP adresi, tarayıcı ve
        cihaz bilgileri, çerez veya reklam tıklaması tanımlayıcıları, form kategorisi ve yinelenen olayları önlemek için
        kullanılan bir talep tanımlayıcısını içerebilir. Özel Pixel olay verilerimize form içeriğini, adınızı, e-posta
        adresinizi, telefon numaranızı veya mesajınızı dahil etmiyoruz.
      </p>
      <p>
        Meta, bu olayları Facebook veya Instagram hesabınız dahil elindeki bilgilerle ilişkilendirebilir ve kendi
        politikaları kapsamında ölçüm, hedef kitle oluşturma ve kişiselleştirilmiş reklamlar için kullanabilir.
        Meta, bilgileri ABD dahil Türkiye dışındaki ülkelerde işleyebilir. Meta'nın veri işlemesi ve tercihlerinize
        ilişkin ayrıntılar için{" "}
        <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer">Meta Gizlilik İlkesi</a>{" "}
        metnini inceleyebilirsiniz.
      </p>
      <p>
        Sitenin alt bölümündeki çerez tercihleri kontrolünden pazarlama izninizi istediğiniz zaman geri çekebilirsiniz.
        Bu işlem, siteye veya formlara erişiminizi engellemeden bu siteden sonraki Pixel olaylarının gönderilmesini
        durdurur. İznin geri çekilmesi, önceden kaydedilmiş çerezleri veya Meta'nın daha önce aldığı bilgileri
        kendiliğinden silmez. İlgili çerezler ve silme seçenekleri Çerez Politikamızda açıklanmıştır.
      </p>
      <h4>Ziyaretçi Tanımlama ve İzleme</h4>
      <p>
        Web sitemizi ziyaret ettiğinizde veya oturum açtığınızda, çevrimiçi veri ortaklarımız veya satıcılarımız
        tarafından bu faaliyetleri, e-posta adresinizle ilişkilendirme dahil olmak üzere, sizinle ilgili sahip oldukları
        veya başkalarının sahip olduğu diğer kişisel bilgilerle ilişkilendirmek için çerezler ve benzer teknolojiler
        kullanılabilir. Daha sonra biz (veya bizim adımıza hizmet sağlayıcılar) bu e-posta adreslerine iletişim ve
        pazarlama gönderebiliriz. Bu iletileri durdurmamızı istemek için{" "}
        <PrivacyRequestButton requestType={PRIVACY_REQUEST_TYPES.MARKETING_OPT_OUT}/> formunu kullanabilirsiniz.
        Kişisel verilerinizin toplanmasına ve işlenmesine ilişkin itirazınızı ise{" "}
        <PrivacyRequestButton requestType={PRIVACY_REQUEST_TYPES.DATA_OBJECTION}/> formundan iletebilirsiniz.
        Ekibimiz talebinizi değerlendirecek ve sonuç hakkında sizinle iletişime geçecektir.
      </p>

      <h3>6. Bilgilerinizi Nasıl İfşa Ediyoruz</h3>
      <p>Bilgilerinizi aşağıdaki şekillerde ifşa ederiz:</p>
      <ul>
        <li>
          <strong>Hizmet Sağlayıcılar</strong>: Site ve Hizmetlerimizi sunmamıza veya iyileştirmemize yardımcı olan
          veya bizim adımıza hizmet sunan belirli üçüncü taraflara bilgilerinize erişim sağlarız veya bilgilerinizi ifşa
          ederiz; bunlar arasında bulut barındırma, faturalama ve ödeme, reklam ve pazarlama, analitik, dolandırıcılığın
          önlenmesi, araştırma, veri depolama veya işleme, güvenlik, yapay zeka modeli eğitim hizmetleri ve danışmanlık
          ve yasal hizmetler bulunur. Kurumsal müşteri verileri ve Google Workspace verileri, yapay zekâ modeli
          eğitimi amacıyla yapılan paylaşımların dışındadır.
        </li>
        <li>
          <strong>İştirakleri ve Bağlı Kuruluşlar</strong>: Bu Politikada belirtilen amaçlar için topladığımız
          bilgileri Palmate şirketler ailesi içinde ifşa edebiliriz.
        </li>
        <li>
          <strong>Şirketiniz</strong>: Yetkili Kullanıcıysanız, şirketinizle olan geçerli Hizmet Sözleşmemizi yerine
          getirmek ve uygulamak ve şirketinizi kullanım, destek veya eğitim ihtiyaçları konusunda bilgilendirmek için
          bilgilerinizi çalıştığınız veya istihdam edildiğiniz şirkete sağlayabiliriz.
        </li>
        <li>
          <strong>Palmate'in ve Diğerlerinin Korunması</strong>: Site veya Hizmetleri kullanarak, yürürlükteki
          yasaların gerektirmesi veya bu tür erişim, saklama veya ifşanın makul ölçüde gerekli olduğuna dair iyi niyetli
          bir inanç halinde, sizinle ilgili topladığımız ve sakladığımız bilgilere erişebileceğimizi, bunları
          saklayabileceğimizi ve ifşa edebileceğimizi kabul ve onaylarsınız: (a) yasal sürece uymak (örn. mahkeme celbi
          veya mahkeme emri); (b) sizinle olan sözleşmeleri uygulamak; (c) herhangi bir içeriğin üçüncü tarafların
          haklarını ihlal ettiği iddialarına yanıt vermek; ve/veya (d) Palmate'in, temsilcilerinin ve iştiraklerin,
          kullanıcılarının ve/veya kamunun hak, mülkiyet veya kişisel güvenliğini korumak.
        </li>
        <li>
          <strong>İş İşlemleri</strong>: Yürürlükteki yasal yükümlülüklere uygun olarak, Palmate'in tamamını veya bir
          kısmını veya varlıklarımızı içeren bir birleşme veya satın alma ile (iflas veya iflas işlemleri kapsamında
          yapılan transferler dahil) bağlantılı olarak veya kurumsal yeniden yapılanma veya hisse satışı veya kurumsal
          kontrolde veya iş değişikliğinde başka bir değişikliğin parçası olarak bilgileriniz üçüncü taraflara
          sağlanabilir; bu, söz konusu işleme veya iş ilişkisine devam edip etmemeye karar vermek amacını da içerir.
        </li>
        <li>
          <strong>Toplu/Kimliksizleştirilmiş Bilgiler</strong>: Zaman zaman, Sitenin veya Hizmetlerin kullanımı veya
          kullanıcı tabanımız hakkında Toplu/Kimliksizleştirilmiş Bilgileri ortaklarla ve diğerleriyle takdirimize göre
          ifşa edebiliriz, ancak bu tür bilgiler sizi kişisel olarak tanımlamayacaktır.
        </li>
        <li>
          <strong>Onayınız veya Talimatınızla</strong>: Bu Politikada açıklanan ifşalara ek olarak, ne zaman onay
          verirseniz veya talimat verirseniz, üçüncü taraflara sizinle ilgili bilgileri ifşa edebiliriz.
        </li>
      </ul>

      <h3>SMS ve Mobil Mesajlaşma</h3>
      <p>
        Palamar Teknoloji Anonim Şirketi, Palmate markası altında hesap doğrulama, kimlik doğrulama, tek kullanımlık
        şifreler (OTP), güvenlik bildirimleri, hesap ve hizmet bildirimleri, müşteri destek iletişimleri ve kullanıcı
        tarafından talep edilen diğer işlemsel mesajlaşmalar için cep telefonu numarası toplayabilir. Mesaj sıklığı
        kullanıcı faaliyetlerine göre değişebilir ve mesaj/veri ücretleri uygulanabilir.
      </p>
      <p>
        Kullanıcının pazarlama mesajları almak için ayrıca açık rıza vermesi halinde Palmate; ürün veya hizmet
        duyuruları, teklifler, promosyonlar, etkinlik davetleri, kampanya mesajları ve benzeri pazarlama iletişimleri
        gönderebilir. Pazarlama SMS'lerine izin vermek isteğe bağlıdır ve Palmate hizmetlerini satın almanın veya
        kullanmanın şartı değildir. Pazarlama mesajları yalnızca ilgili opt-in sürecini tamamlayan kullanıcılara
        gönderilir ve kullanıcılar STOP yanıtını vererek bu izni istedikleri zaman geri çekebilir.
      </p>
      <p>
        SMS onay verilerinizi veya kişisel bilgilerinizi pazarlama amacıyla üçüncü taraflara satmayız ya da paylaşmayız.
        Cep telefonu numaralarını, SMS izin kayıtlarını veya opt-in verilerini bağlı kuruluşlarla ya da üçüncü
        taraflarla kendi pazarlama veya tanıtım amaçları için paylaşmayız.
      </p>

      <h3>7. Bilgilerinizin Saklanması</h3>
      <p>
        Bilgilerinizi, işlendiği amaçlar için gerekenden daha uzun süre saklamayız. Bilgileri sakladığımız süre, bunları
        topladığımız ve kullandığımız amaçlara ve/veya yürürlükteki yasalara ve yasal yükümlülüklerimize uymak için
        gerektiği şekilde bağlıdır.
      </p>

      <h3>8. Haklarınız ve Seçimleriniz</h3>
      <p>Bu bölümde daha ayrıntılı olarak açıklandığı gibi, bilgilerinizle ilgili belirli haklarınız vardır.</p>
      <h4>Yasal Haklarınız</h4>
      <p>
        Yürürlükteki yasa kapsamındaki yasal haklarınız hakkında daha fazla bilgi almak veya bunlardan herhangi birini
        kullanmak isterseniz, lütfen her zaman aşağıdaki "İletişim Bilgileri" bölümündeki bilgileri kullanarak bizimle
        iletişime geçin. Yerel yasalarınız (örneğin, Avrupa Ekonomik Alanı, Kaliforniya, Connecticut veya benzer
        yasalara sahip diğer bölgelerin vatandaşı veya sakini iseniz), bizden şunları talep etmenize izin verebilir:
      </p>
      <ul>
        <li>Hakkınızda tuttuğumuz belirli bilgilere erişim ve/veya bunun bir kopyasını sağlamamız</li>
        <li>Güncel olmayan veya yanlış bilgileri güncellememiz</li>
        <li>Hakkınızda tuttuğumuz belirli bilgileri silmemiz</li>
        <li>Belirli bilgilerinizi işleme ve ifşa etme şeklimizi kısıtlamamız</li>
        <li>Verilerinizi başka bir hizmet sağlayıcıya taşımanız</li>
      </ul>
      <p>
        Bu tür yasalar ayrıca belirli amaçlar için bilgilerinizin işlenmesi için verdiğiniz onayı geri çekmenize izin
        verebilir. Tüm talepleri değerlendireceğiz ve yanıtımızı yürürlükteki yasanın belirttiği süre içinde ve
        yürürlükteki yasanın gerektirdiği şekilde sağlayacağız.
      </p>
      <h4>Pazarlama İletişimleri</h4>
      <p>
        Yürürlükteki yasal gerekliliklere uygun olarak, size Hizmetlerimiz veya ilginizi çekeceğine inandığımız üçüncü
        tarafların hizmetleri ile ilgili pazarlama iletişimleri gönderirsek, her zaman aşağıdaki "İletişim Bilgileri"
        bölümündeki bilgileri kullanarak bizimle iletişime geçerek bu tür iletişimleri göndermeyi durdurmamızı
        isteyebilirsiniz. Pazarlama e-posta mesajlarımızda, bu tür e-postaların altında bulunan talimatları izleyerek de
        vazgeçebilirsiniz.
      </p>

      <h3>9. Üçüncü Taraf Bağlantıları ve Özellikleri</h3>
      <p>
        Hizmetlerimiz, yapay zeka modeli sağlayıcıları, bulut hizmetleri ve entegrasyon ortakları dahil olmak üzere
        üçüncü taraf web sitelerine bağlantılar içerebilir. Bu siteleri ziyaret etmeyi ve hizmetlerini kullanmayı
        seçerseniz, lütfen içeriklerinden veya gizlilik uygulamalarından sorumlu olmadığımızı unutmayın. Bilgilerinizin
        toplanması, kullanılması ve ifşa edilmesi, üçüncü taraf web sitelerinin gizlilik politikalarına tabi olacaktır,
        bu Politikaya değil.
      </p>

      <h3>10. Uluslararası Kullanıcılar</h3>
      <p>
        Bilgileriniz, bizim ve üçüncü taraf hizmet sağlayıcılarımız tarafından Türkiye, Amerika Birleşik Devletleri ve
        başka yerlerde saklanır ve işlenir. Dolayısıyla, bilgileriniz, ikamet ettiğiniz ülkedekinden farklı veri koruma
        yasalarına sahip olabilecek yargı bölgelerinde saklanabilir, işlenebilir ve depolanabilir. Bilgilerinizin bu
        şekillerde aktarılması durumunda, lütfen sınır ötesi bilgi aktarımını yöneten geçerli yasal gerekliliklere
        uyduğumuzu unutmayın. Hizmetleri kullanarak, bu transferleri kabul eder ve onaylarsınız.
      </p>

      <h3>11. Bilgilerinizi Nasıl Koruyoruz</h3>
      <p>
        Palmate, bize sağlanan bilgileri kaybolma, kötüye kullanma ve yetkisiz erişim, ifşa, değiştirme veya yok etmeden
        korumak için çeşitli teknik ve organizasyonel güvenlik önlemleri alır. Bu önlemler arasında şifreleme, erişim
        kontrolleri, güvenli geliştirme uygulamaları ve düzenli güvenlik değerlendirmeleri bulunur. Ancak, hiçbir
        İnternet veya e-posta iletimi hiçbir zaman tamamen güvenli veya hatasız değildir. Lütfen çevrimiçi olarak bize
        herhangi bir bilgi verirken bunu aklınızda bulundurun.
      </p>

      <h3>12. Kaliforniya Kullanıcıları İçin Gizlilik Bilgileri</h3>
      <p>
        Kaliforniya'da ikamet ediyorsanız, Kaliforniya Tüketici Gizlilik Yasası ("CCPA"), CCPA'da tanımlanan "kişisel
        bilgileriniz" ile ilgili haklarınız konusunda size ek bilgi sağlamamızı gerektirir.
      </p>
      {/* Türkçe CCPA kategori tablosu içeriği kullanıcı tarafından eksik sağlandığı için burada gösterilmiyor. */}

      <h3>13. Politikamızdaki Değişiklikler</h3>
      <p>
        Yasa, veri toplama ve kullanım uygulamalarımız veya teknolojideki gelişmelerdeki değişiklikleri yansıtmak için
        bu Politikayı istediğimiz zaman değiştirme hakkını saklı tutarız. Revize edilmiş Politikayı Hizmetlerde
        erişilebilir hale getireceğiz; bu nedenle Politikayı periyodik olarak gözden geçirmelisiniz. Politikanın en son
        incelediğiniz zamandan bu yana değişip değişmediğini belgenin başındaki "Son Güncelleme" tarihine bakarak
        anlayabilirsiniz. Politika üzerinde önemli bir değişiklik yaparsak, yasal gerekliliklere uygun olarak uygun
        bildirim sağlanacaktır.
      </p>

      <h3>14. GOOGLE WORKSPACE VERİLERİ</h3>
      <p>
        Bir Gmail posta kutusunu Palmate'e bağladığınızda, Palmate'in Google API'lerinden aldığı bilgileri kullanımı,
        Sınırlı Kullanım (Limited Use) gereklilikleri dâhil olmak üzere Google API Hizmetleri Kullanıcı Verileri
        Politikası'na (Google API Services User Data Policy) uygundur.
      </p>
      <p>Bu politikadaki diğer hükümlere bakılmaksızın, özellikle:</p>
      <ul>
        <li>
          Google Workspace verilerini (ham, toplulaştırılmış, kimlikten arındırılmış veya türetilmiş olsun)
          bize ya da üçüncü bir tarafa ait herhangi bir genel amaçlı veya temel makine öğrenmesi ya da yapay zekâ
          modelini oluşturmak, eğitmek veya geliştirmek için kullanmayız, aktarmayız veya satmayız.
        </li>
        <li>
          Google Workspace verileri, bu politikanın diğer bölümlerinde açıklanan toplulaştırılmış ve kimlikten
          arındırılmış analizlerin ve hemen aşağıda belirtilenler dışında bu politikadaki tüm amaçların kapsamı
          dışındadır.
        </li>
        <li>
          Google Workspace verilerini yalnızca talep ettiğiniz özellikleri sunmak için işleriz: müşterilerinizin
          size gönderdiği e-postaları kendi destek temsilcilerinize göstermek, posta kutunuzdan gönderilecek
          yanıtları hazırlamak ve göndermek ve daha sonra ibraz edilebilmesi için mesajın aslını saklamak.
        </li>
        <li>
          Google Workspace mesajlarının içeriğini, Google'ın bu içeriği modellerini eğitmek veya geliştirmek için
          kullanmadığı koşullar altında, ücretli hizmet kapsamında Google'ın Gemini API'sine
          (gemini-3.1-flash-lite modeli) aktarırız. Bu içeriği başka hiçbir yapay zekâ veya makine öğrenmesi
          sağlayıcısına aktarmayız.
        </li>
        <li>
          Kanunen zorunlu olduğumuz durumlar dışında, Google Workspace verilerini kendi Palmate çalışma alanınızın
          kullanıcıları dışındaki hiçbir kişiye aktarmayız.
        </li>
        <li>
          Her mesajın aslını 2 yıl boyunca saklarız ve bu sürenin sonunda sileriz. Posta kutusunun bağlantısının
          kesilmesi, saklanan kimlik doğrulama bilgilerinin silinmesini ve erişimimizin derhâl sona ermesini sağlar.
        </li>
      </ul>

      <h3>15. İletişim Bilgileri</h3>
      <p>
        Palmate, bu Politika ve bilgilerinizin toplanması ve kullanımı ile ilgili yorumlarınızı ve sorularınızı
        memnuniyetle karşılar. Sorularınız veya endişeleriniz varsa, lütfen bize
        <a href="mailto:privacy@palmate.ai"> privacy@palmate.ai</a> adresinden e-posta gönderin veya Veri Koruma
        Görevlimize şu adrese yazın:
      </p>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
        <br />
        Gizlilik Departmanı
        <br />
        Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul
      </p>
      <p>
        Avrupa Ekonomik Alanı'ndaki kullanıcılar, Veri Koruma Görevlimize şu adresten de ulaşabilir:
        <a href="mailto:dpo@palmate.ai"> dpo@palmate.ai</a>
      </p>
    </div>
  );
}
